import { createApp, type Directive } from 'vue';
import App from './App.vue';
import router from './router'; // 引入路由配置
import { createPinia } from 'pinia';
import i18n from './i18n';
import languageManager from '@/scripts/assets/scripts/languageManager'; // 引入语言管理器
import { localizedImage } from '@/scripts/assets/scripts/localizedImage'; // 引入语言管理器
import { Lazyload, Overlay } from 'vant';
import 'vant/es/toast/style';
import 'vant/es/toast/index.css';
import yinqing2 from '@/assets/sounds/yinqing2.mp3';
import { isRunApp } from '@/scripts/assets/scripts/utils';
import { WebService } from '@/scripts/assets/scripts/platform/WebService';
import * as Sentry from '@sentry/vue';

// main.ts 或其他入口文件中顶部添加
const params = window.location.href;
const isDebug = params.indexOf('hsNhCY') > 0;

if (import.meta.env.DEV || isDebug) {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/vconsole@latest/dist/vconsole.min.js';
    script.onload = () => {
        try {
            // @ts-ignore 忽略类型检查
            new window.VConsole();
            console.log('[vConsole] 本地模式已启用');
        } catch (error) {
            console.error('vConsole 初始化失败:', error);
        }
    };
    script.onerror = () => {
        console.error('vConsole 资源加载失败');
    };
    document.body.appendChild(script);
    if (import.meta.env.DEV) {
        window.smallName = 'forra77';
        window.gameName = 'FORRA77.BET';
        window.appTheme = 'deepPurple';
        window.language = 'pt-BR';
    }
} else {
    //正式环境去除
    console.log = () => {};
    console.error = () => {};
}

function setupServiceWorker() {
    if (!('serviceWorker' in navigator) || isRunApp()) return;
    navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then(reg => console.log('SW 注册成功:', reg.scope))
        .catch(err => console.error('SW 注册失败:', err));
}

if (document.readyState === 'complete') {
    setupServiceWorker();
} else {
    window.addEventListener('load', setupServiceWorker);
}

const app = createApp(App);
// languageManager.initialize(app).then(() => {
//     console.log('语言配置状态：', languageManager.getLocaleInfo());
// }).catch((error) => {
//     console.error('语言初始化失败:', error);
// });

//1：标签指令点击声音 v-click-sound
//2：代码调用
// 导入: import { getCurrentInstance } from 'vue';
// 调用: getCurrentInstance()?.proxy?.clickSound?.();
let sound: HTMLAudioElement | null = null;
const initClickSound = () => {
    if (!sound) {
        sound = new Audio(yinqing2);
        sound.preload = 'auto';
        sound.load();
        sound
            .play()
            .then(() => {
                sound!.pause();
                sound!.currentTime = 0;
            })
            .catch(e => {
                console.log('首次播放用于解锁音频失败：', e);
            });
    }
};

const clickSound = () => {
    // if (!sound) {
    //     sound = new Audio(yinqing2);
    // }
    if (sound) {
        sound.currentTime = 0;
        sound.play().catch(e => console.log('Failed to play sound:', e));
    }
};
const clickSoundDirective: Directive = {
    mounted(el, binding) {
        el.addEventListener('click', () => {
            clickSound();
        });
    },
    unmounted(el) {
        //页面销毁
        //el.removeEventListener('click', playSound);
    },
};
// app.mixin({
//     methods: {
//         clickSound,
//     },
// });
// app.directive('click-sound', clickSoundDirective);
// initSentry();
// const pinia = createPinia();
// app.use(pinia);
// app.use(router);
// app.use(i18n);
// app.use(Overlay);
// app.use(Lazyload, {
//     lazyComponent: true,
// });
// app.mount('#app');
function isPCFun(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    const toMatch = ['android', 'iphone', 'ipad', 'ipod', 'windows phone', 'mobile', 'mobi'];

    // 如果 UA 中包含移动设备关键词，则判断为移动端
    for (const keyword of toMatch) {
        if (ua.includes(keyword)) return false;
    }

    // 如果 UA 看不出移动端，再判断平台类型（navigator.platform 比较靠谱）
    const platform = navigator.platform.toLowerCase();
    const desktopPlatforms = ['win32', 'macintel', 'linux x86_64'];

    if (desktopPlatforms.includes(platform)) return true;

    // 最后保险起见，根据屏幕尺寸判断（不是核心）
    return window.innerWidth >= 768;
}

declare global {
    interface Window {
        isPC: boolean;
    }
}

function updateViewport() {
    const designWidth = 390; // 设计稿宽度
    const designHeight = 844; // 设计稿高度

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const html = document.documentElement;
    const body = document.body;

    // 判断是否是 PC
    const isPC = isPCFun();
    window['isPC'] = isPC;

    if (isPC) {
        const baseFontSize = 47.5; // 设计时设定 1rem = 39px
        // PC 上：以高度为基准，保持设计比例
        const scale = vh / designHeight;
        const contentWidth = scale * 475;
        const fontSize = scale * baseFontSize;

        html.style.height = vh + 'px';
        html.style.fontSize = fontSize + 'px';
        body.style.height = vh + 'px';

        // 设置内容区域宽高比（你可以在CSS中设置#app居中）
        const app = document.getElementById('app');
        if (app) {
            app.style.width = contentWidth + 'px';
            app.style.height = '100%';
            app.style.margin = '0 auto';
        }
    } else {
        // 移动端：以宽度为基准，常规适配
        const baseFontSize = 39; // 设计时设定 1rem = 47.5px
        const scale = vw / designWidth;
        const fontSize = scale * baseFontSize;

        html.style.fontSize = fontSize + 'px';
        html.style.height = vh + 'px';
        body.style.height = vh + 'px';
        const app = document.getElementById('app');
        if (app) {
            app.style.width = '100%';
            app.style.height = '100%';
            app.style.margin = '0';
            app.style.top = '0';
        }
    }
}
updateViewport();

window.addEventListener('resize', () => {
    setTimeout(updateViewport, 150);
});

document.addEventListener(
    'click',
    () => {
        initClickSound();
    },
    { once: true }
);

const EXPIRE_TIME = 60 * 60 * 1000; // 1小时
let lastHiddenTime: number | null = null;
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
        // 页面切到后台时，记录当前时间
        lastHiddenTime = Date.now();
        console.log('用户切后台', lastHiddenTime);
    } else if (document.visibilityState === 'visible' && lastHiddenTime) {
        // 页面切回前台时，比较时间差
        const now = Date.now();
        const hiddenDuration = now - lastHiddenTime;
        console.log('用户切前台', now, hiddenDuration);
        if (hiddenDuration > EXPIRE_TIME) {
            const restartSuccess = WebService.appRestartApp();
            if (!restartSuccess) {
                console.log('超时刷新页面');
                location.reload();
            }
        }
        lastHiddenTime = null!; // 重置时间（可选）
    }
});

function initSentry() {
    if (import.meta.env.DEV) {
        return;
    }
    Sentry.init({
        app,
        dsn: 'https://74a13ab3d9eb9bae6ddfdff9c5d209b9@o4506398354898944.ingest.us.sentry.io/4510006480470016',
        sendDefaultPii: true,
        debug: true,
        release: import.meta.env.VITE_APP_VERSION,
    });

    window.addEventListener(
        'error',
        event => {
            const target = event.target as HTMLElement | null;
            if (!target) return;

            if (target instanceof HTMLImageElement) {
                Sentry.captureException(new Error(`资源加载失败: IMG -> ${target.src}`));
            } else if (target instanceof HTMLLinkElement) {
                Sentry.captureException(new Error(`资源加载失败: LINK -> ${target.href}`));
            } else if (target instanceof HTMLScriptElement) {
                Sentry.captureException(new Error(`资源加载失败: SCRIPT -> ${target.src}`));
            }
        },
        true
    );
}

async function bootstrap() {
    try {
        await languageManager.initialize(app);
        console.log('语言配置状态：', languageManager.getLocaleInfo());
    } catch (error) {
        console.error('语言初始化失败:', error);
    }
    app.mixin({
        methods: { clickSound },
    });
    app.directive('localized-image', localizedImage);
    app.directive('click-sound', clickSoundDirective);
    const pinia = createPinia();
    app.use(pinia);
    app.use(router);
    app.use(Overlay);
    app.use(Lazyload, { lazyComponent: true });
    app.mount('#app');
}
bootstrap();
